import { useEffect } from "react";

/**
 * Dependency-free client-side head synchroniser (replaces react-helmet-async).
 *
 * SSR metadata is owned entirely by each route's `head()` option
 * (see src/lib/seo/routeHead.ts + src/lib/seo/contentHead.ts), which TanStack
 * Start renders into the server HTML. This component only mirrors
 * page-computed values into the live document for client-side navigations and
 * for data-derived tags (JSON-LD, article:* meta) that a route `head()` cannot
 * know ahead of time. It never emits duplicate tags: existing tags are updated
 * in place and restored on unmount.
 */

export interface HeadMetaTag {
  name?: string;
  property?: string;
  content?: string | null;
}

export interface PageHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  meta?: HeadMetaTag[];
  /** JSON-LD objects appended as <script type="application/ld+json"> */
  jsonLd?: unknown[];
}

const setMeta = (tag: HeadMetaTag): (() => void) => {
  if (!tag.content) return () => {};
  const attr = tag.property ? "property" : "name";
  const key = tag.property ?? tag.name;
  if (!key) return () => {};

  const selector = `meta[${attr}="${CSS.escape(key)}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (existing) {
    const previous = existing.getAttribute("content");
    const wasManaged = existing.hasAttribute("data-page-head");
    existing.setAttribute("content", tag.content);
    // Marks the tag as page-provided (not the SSR fallback) so
    // scripts/generate-seo-route-meta.py can tell the two apart.
    existing.setAttribute("data-page-head", "");
    return () => {
      if (previous === null) existing.removeAttribute("content");
      else existing.setAttribute("content", previous);
      if (!wasManaged) existing.removeAttribute("data-page-head");
    };
  }

  const created = document.createElement("meta");
  created.setAttribute(attr, key);
  created.setAttribute("content", tag.content);
  created.setAttribute("data-page-head", "");
  document.head.appendChild(created);
  return () => created.remove();
};

const PageHead = ({ title, description, canonical, meta = [], jsonLd = [] }: PageHeadProps) => {
  const signature = JSON.stringify({ title, description, canonical, meta, jsonLd });

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    if (title) {
      const previousTitle = document.title;
      document.title = title;
      const titleEl = document.head.querySelector("title");
      const titleWasManaged = titleEl?.hasAttribute("data-page-head") ?? false;
      titleEl?.setAttribute("data-page-head", "");
      cleanups.push(() => {
        document.title = previousTitle;
        if (!titleWasManaged) titleEl?.removeAttribute("data-page-head");
      });
    }

    const tags: HeadMetaTag[] = [
      ...(description ? [{ name: "description", content: description }] : []),
      ...meta,
    ];
    for (const tag of tags) cleanups.push(setMeta(tag));

    if (canonical) {
      const link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (link) {
        const previous = link.getAttribute("href");
        link.setAttribute("href", canonical);
        link.setAttribute("data-page-head", "");
        cleanups.push(() => {
          if (previous === null) link.removeAttribute("href");
          else link.setAttribute("href", previous);
        });
      } else {
        const created = document.createElement("link");
        created.setAttribute("rel", "canonical");
        created.setAttribute("href", canonical);
        created.setAttribute("data-page-head", "");
        document.head.appendChild(created);
        cleanups.push(() => created.remove());
      }
    }

    for (const data of jsonLd) {
      if (!data) continue;
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-head", "");
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      cleanups.push(() => script.remove());
    }

    return () => {
      for (const cleanup of cleanups.reverse()) cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return null;
};

export default PageHead;

/** Convenience wrapper for pages that only need to add JSON-LD. */
export const JsonLd = ({ data }: { data: unknown }) => <PageHead jsonLd={[data]} />;
